/**
 * Project set-up rules (Project settings, help `field.project.settings`): what a new aircraft, crew member or
 * position needs, and what a project opened from its folder (no TJR job) still lacks before a flight's daily entry
 * can be saved. Messages are Australian English, sentence case, and say what to fix.
 */
import type { Aircraft, Person, Position, ValidationIssue } from './model.js';
import { holdsRole, isActivePerson } from './staff.js';
import { validateAircraftPerformance } from './aircraft-performance.js';

export type PositionKind = Position['kind'];

/** The words the crew use for each position kind. */
export const POSITION_KIND_LABEL: Readonly<Record<PositionKind, string>> = {
  base_mag: 'Base mag',
  ground_cal: 'Ground cal',
  test_line: 'Test line',
};

const live = <T extends { readonly id: string; readonly deleted_at: string | null }>(
  rows: readonly T[],
  self: T,
): T[] => rows.filter((r) => r.id !== self.id && r.deleted_at === null);

const same = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/** A new or edited aircraft: registration required and unique, rates and hours zero or more. */
export function validateAircraft(aircraft: Aircraft, existing: readonly Aircraft[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const reg = aircraft.registration.trim();
  if (reg === '') issues.push({ field: 'registration', message: 'Enter the aircraft registration.' });
  else if (live(existing, aircraft).some((a) => same(a.registration, reg))) {
    issues.push({ field: 'registration', message: `${reg.toUpperCase()} is already in the aircraft list.` });
  }
  const checks: ReadonlyArray<[keyof Aircraft & string, number | null, string]> = [
    ['fuel_flow_lph', aircraft.fuel_flow_lph, 'Fuel flow must be more than 0 L/h.'],
    ['mr_interval_h', aircraft.mr_interval_h, 'MR interval must be more than 0 h.'],
    ['max_flight_h', aircraft.max_flight_h ?? null, 'Maximum flight time must be more than 0 h.'],
    ['fuel_capacity_l', aircraft.fuel_capacity_l ?? null, 'Fuel capacity must be more than 0 L.'],
  ];
  for (const [field, value, message] of checks) {
    if (value !== null && !(Number.isFinite(value) && value > 0)) issues.push({ field, message });
  }
  for (const field of ['ttis_at_issue_h', 'ttis_at_start_h'] as const) {
    const value = aircraft[field];
    if (value !== null && !(Number.isFinite(value) && value >= 0)) {
      issues.push({ field, message: 'TTIS must be zero or more hours.' });
    }
  }
  // TJR RS 64804 ("Issue TTIS is less than Start TTIS", wording inverted: it fires when issue is greater).
  const issue = aircraft.ttis_at_issue_h;
  const start = aircraft.ttis_at_start_h;
  if (issue !== null && start !== null && issue >= 0 && start >= 0 && issue > start) {
    issues.push({
      field: 'ttis_at_issue_h',
      message:
        'TTIS at MR issue cannot be more than TTIS at start: the MR was issued before the project started.',
    });
  }
  // the JSA's empty weight, MTOW and reserve kept in the register (decision 863, 6 B)
  issues.push(...validateAircraftPerformance(aircraft));
  return issues;
}

/**
 * A new or edited position: name required and unique within its kind; easting and northing, when given, are
 * metres, zero or more (MGA/UTM grid coordinates).
 */
export function validatePosition(position: Position, existing: readonly Position[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const kind = POSITION_KIND_LABEL[position.kind].toLowerCase();
  const name = position.name.trim();
  if (name === '') issues.push({ field: 'name', message: `Enter the ${kind} position name.` });
  else if (live(existing, position).some((p) => p.kind === position.kind && same(p.name, name))) {
    issues.push({ field: 'name', message: `A ${kind} position named ${name} already exists.` });
  }
  for (const [field, word] of [
    ['easting', 'Easting'],
    ['northing', 'Northing'],
    ['easting_end', 'End easting'],
    ['northing_end', 'End northing'],
  ] as const) {
    const value = position[field];
    if (value != null && !(Number.isFinite(value) && value >= 0)) {
      issues.push({ field, message: `${word} must be zero or more metres.` });
    }
  }
  if ((position.easting_end == null) !== (position.northing_end == null))
    issues.push({
      field: 'easting_end',
      message: 'Enter both the end easting and the end northing, or neither.',
    });
  return issues;
}

/** What a flight's daily entry needs from Project settings (validateDailyEntry: pilot, aircraft, operator, base mag). */
export type SetupGap = 'aircraft' | 'pilot' | 'operator' | 'base_mag';

/**
 * Set-up still missing before a flight with survey hours can be entered: an aircraft and a pilot for any flight,
 * an operator and a base mag position when it has survey, comp-box or cal hours (TJR 5.1 as `validateDailyEntry`).
 */
export function dailyEntrySetupGaps(
  aircraft: readonly Aircraft[],
  people: readonly Person[],
  positions: readonly Position[],
): SetupGap[] {
  const gaps: SetupGap[] = [];
  const has = <T extends { readonly deleted_at: string | null }>(rows: readonly T[], ok: (r: T) => boolean) =>
    rows.some((r) => r.deleted_at === null && ok(r));
  if (!has(aircraft, () => true)) gaps.push('aircraft');
  if (!has(people, (p) => isActivePerson(p) && holdsRole(p, 'pilot'))) gaps.push('pilot');
  if (!has(people, (p) => isActivePerson(p) && holdsRole(p, 'operator'))) gaps.push('operator');
  if (!has(positions, (p) => p.kind === 'base_mag')) gaps.push('base_mag');
  return gaps;
}

const GAP_WORDS: Readonly<Record<SetupGap, string>> = {
  aircraft: 'an aircraft',
  pilot: 'a pilot',
  operator: 'an operator',
  base_mag: 'a base mag position',
};

/** "Add an aircraft and a pilot in Project settings first." for the given gaps; null when nothing is missing. */
export function setupGapMessage(gaps: readonly SetupGap[]): string | null {
  if (gaps.length === 0) return null;
  const words = gaps.map((g) => GAP_WORDS[g]);
  const list =
    words.length === 1 ? words[0]! : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]!}`;
  return `Add ${list} in Project settings first.`;
}
